import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Target,
  Plus,
  Edit,
  Trash2,
  Download,
  RefreshCw,
  Filter,
  Calendar,
  User
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency } from '@/lib/utils';
import { SetRevenueTargetModal } from '@/components/revenue/SetRevenueTargetModal';
import { RevenueProgressCircle } from '@/components/revenue/RevenueProgressCircle';
import { RevenueTarget } from '@/types/revenue';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const RevenueTargetsPage: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<RevenueTarget | undefined>();
  const [filters, setFilters] = useState({
    month: '',
    userId: ''
  });

  // Fetch all targets
  const {
    data: targets,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ['revenue-targets', filters],
    queryFn: async (): Promise<RevenueTarget[]> => {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.month) params.append('month', filters.month);
      if (filters.userId) params.append('userId', filters.userId);

      const response = await fetch(`${API_URL}/revenue-targets?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch targets');
      const result = await response.json();
      return result.data || [];
    }
  });

  // Fetch users for filter dropdown
  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const result = await response.json();
      return result.data || [];
    }
  });

  const [isSyncing, setIsSyncing] = useState(false);

  // Sync achieved from live WON deals
  const handleSyncAchieved = async () => {
    setIsSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const body = filters.month ? JSON.stringify({ month: filters.month }) : JSON.stringify({});
      const response = await fetch(`${API_URL}/revenue-targets/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Sync failed');
      toast({
        title: 'Achieved synced',
        description: result.message || `${result.data?.updated || 0} records updated from WON deals`
      });
      queryClient.invalidateQueries({ queryKey: ['revenue-targets'] });
    } catch (err: any) {
      toast({ title: 'Sync failed', description: err.message, variant: 'destructive' });
    } finally {
      setIsSyncing(false);
    }
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/revenue-targets/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete target');
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Revenue target deleted successfully'
      });
      queryClient.invalidateQueries({ queryKey: ['revenue-targets'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const handleEdit = (target: RevenueTarget) => {
    setSelectedTarget(target);
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this revenue target?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleAddNew = () => {
    setSelectedTarget(undefined);
    setIsModalOpen(true);
  };

  const handleExportCSV = () => {
    if (!targets || targets.length === 0) return;

    const csvContent = [
      ['Month', 'User', 'Target', 'Achieved', 'Variance', 'Percentage', 'Notes'],
      ...targets.map((target) => [
        target.month,
        target.user?.name || 'Organization-wide',
        target.target,
        target.achieved,
        target.achieved - target.target,
        ((target.achieved / target.target) * 100).toFixed(2),
        target.notes || ''
      ])
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue-targets-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Target className="h-8 w-8 text-blue-600" />
              Revenue Targets Management
            </h1>
            <p className="text-gray-600 mt-1">
              Set, track, and manage revenue targets for your organization
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => refetch()} aria-label="Refresh targets">
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncAchieved}
              disabled={isSyncing}
              title="Recompute achieved from actual WON deals"
              aria-label="Sync achieved amounts from WON deals"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
              Sync Achieved
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCSV}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button size="sm" onClick={handleAddNew}>
              <Plus className="h-4 w-4 mr-2" />
              Add Target
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="h-4 w-4 inline mr-2" />
                  Month
                </label>
                <Input
                  type="month"
                  value={filters.month}
                  onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                  placeholder="All months"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="h-4 w-4 inline mr-2" />
                  User
                </label>
                <Select
                  value={filters.userId}
                  onValueChange={(value) => setFilters({ ...filters, userId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All users</SelectItem>
                    {users?.map((u: any) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Targets Table */}
        <Card>
          <CardHeader>
            <CardTitle>Revenue Targets</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : !targets || targets.length === 0 ? (
              <div className="text-center py-12">
                <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Revenue Targets Found
                </h3>
                <p className="text-gray-600 mb-4">
                  Get started by creating your first revenue target.
                </p>
                <Button onClick={handleAddNew}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Target
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-sm font-medium text-gray-700">
                        Month
                      </th>
                      <th className="text-left p-3 text-sm font-medium text-gray-700">
                        User
                      </th>
                      <th className="text-left p-3 text-sm font-medium text-gray-700">
                        Target
                      </th>
                      <th className="text-left p-3 text-sm font-medium text-gray-700">
                        Achieved
                      </th>
                      <th className="text-left p-3 text-sm font-medium text-gray-700">
                        Variance
                      </th>
                      <th className="text-left p-3 text-sm font-medium text-gray-700">
                        Progress
                      </th>
                      <th className="text-left p-3 text-sm font-medium text-gray-700">
                        Notes
                      </th>
                      <th className="text-left p-3 text-sm font-medium text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {targets.map((target) => {
                      const variance = target.achieved - target.target;
                      const percentage = (target.achieved / target.target) * 100;

                      return (
                        <motion.tr
                          key={target.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="border-b hover:bg-gray-50"
                        >
                          <td className="p-3">
                            {new Date(target.month + '-01').toLocaleDateString('en-US', {
                              month: 'long',
                              year: 'numeric'
                            })}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {target.user ? (
                                <>
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-sm font-medium text-blue-600">
                                      {target.user.name.charAt(0)}
                                    </span>
                                  </div>
                                  <span>{target.user.name}</span>
                                </>
                              ) : (
                                <span className="text-gray-500 italic">
                                  Organization-wide
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-3 font-semibold">
                            {formatCurrency(target.target)}
                          </td>
                          <td className="p-3 font-semibold">
                            {formatCurrency(target.achieved)}
                          </td>
                          <td
                            className={`p-3 font-semibold ${
                              variance >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {variance >= 0 ? '+' : ''}
                            {formatCurrency(variance)}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <RevenueProgressCircle percentage={percentage} size="sm" />
                              <span className="text-sm font-medium">
                                {percentage.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                          <td className="p-3 max-w-xs truncate text-sm text-gray-600">
                            {target.notes || '-'}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(target)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(target.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal */}
      <SetRevenueTargetModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTarget(undefined);
        }}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['revenue-targets'] });
        }}
        existingTarget={selectedTarget}
      />
    </DashboardLayout>
  );
};
